import { assessSingleton } from "./assessSingleton";
import { assessContinuous, ContinuousCoverage } from "./assessContinuous";
import { clipDocToRequirementPeriod } from "./period";
import {
    Period,
    CollectedExtraction,
    CoverageRow,
    Fulfillment,
    RequirementKey,
    RequirementSpec,
    UnmatchedDoc,
} from "./types";

function byDocumentId(a: CollectedExtraction, b: CollectedExtraction): number {
    return a.documentId - b.documentId;
}

function isResolved(doc: CollectedExtraction): boolean {
    return doc.key !== null && doc.isDimensionResolved !== false;
}

function passthroughUnmatched(doc: CollectedExtraction): UnmatchedDoc {
    return {
        documentId: doc.documentId,
        fileName: doc.fileName,
        status: doc.status === "extracting" ? "extracting" : "unrecognized",
        statusMessage: doc.statusMessage,
    };
}

function needsDimensionUnmatched(doc: CollectedExtraction): UnmatchedDoc {
    return {
        documentId: doc.documentId,
        fileName: doc.fileName,
        status: "unrecognized",
        reason: "needs_dimension",
        period: doc.period ?? null,
        summary: doc.summary,
    };
}

function intersectsRequirementPeriod(
    docPeriod: Period,
    requirementPeriod: Period,
): boolean {
    return (
        clipDocToRequirementPeriod(docPeriod, requirementPeriod) !==
        "outside_period"
    );
}

function continuousRequirementPeriods(
    requirements: RequirementSpec[],
): Period[] {
    return requirements.flatMap((requirement) =>
        requirement.kind === "continuous" && requirement.period !== null
            ? [requirement.period]
            : [],
    );
}

// A period mismatch outranks the dimension prompt: resolving the dimension
// could not make the doc usable anyway, so surface the period problem first.
function isOutsideEveryWindow(
    doc: CollectedExtraction,
    requirements: RequirementSpec[],
): boolean {
    if (!doc.period) {
        return false;
    }
    const docPeriod = doc.period;
    const periods = continuousRequirementPeriods(requirements);
    return (
        periods.length > 0 &&
        periods.every(
            (period) => !intersectsRequirementPeriod(docPeriod, period),
        )
    );
}

function outsidePeriodUnmatched(doc: CollectedExtraction): UnmatchedDoc {
    return {
        documentId: doc.documentId,
        fileName: doc.fileName,
        status: "unrecognized",
        reason: "outside_period",
        period: doc.period ?? null,
        summary: doc.summary,
    };
}

function assessRequirement(
    requirement: RequirementSpec,
    docs: CollectedExtraction[],
): ContinuousCoverage {
    if (requirement.kind === "singleton") {
        return assessSingleton(requirement.key, docs);
    }
    return assessContinuous({
        key: requirement.key,
        period: requirement.period,
        docs,
    });
}

export function assessFulfillment(args: {
    requirements: RequirementSpec[];
    extractions: CollectedExtraction[];
}): Fulfillment {
    const { requirements, extractions } = args;
    const rows: CoverageRow[] = [];
    const unmatched: UnmatchedDoc[] = [];
    const uncoveredKeys: RequirementKey[] = [];

    for (const doc of extractions) {
        if (doc.status !== "extracted") {
            unmatched.push(passthroughUnmatched(doc));
        }
    }

    const extracted = extractions.filter((doc) => doc.status === "extracted");
    const resolved = extracted.filter(isResolved);
    const requestedKeys = new Set(requirements.map((req) => req.key));

    for (const requirement of requirements) {
        const docs = resolved.filter((doc) => doc.key === requirement.key);
        const coverage = assessRequirement(requirement, docs);
        rows.push(...coverage.rows);
        unmatched.push(...coverage.unmatched);
        if (!coverage.isCovered) {
            uncoveredKeys.push(requirement.key);
        }
    }

    for (const doc of resolved) {
        if (doc.key !== null && !requestedKeys.has(doc.key)) {
            unmatched.push({
                documentId: doc.documentId,
                fileName: doc.fileName,
                status: "unrecognized",
                reason: "not_needed",
            });
        }
    }

    const unresolvedDocs = extracted
        .filter((doc) => !isResolved(doc))
        .sort(byDocumentId);
    let hasDocNeedingDimension = false;
    for (const doc of unresolvedDocs) {
        if (isOutsideEveryWindow(doc, requirements)) {
            unmatched.push(outsidePeriodUnmatched(doc));
            continue;
        }
        unmatched.push(needsDimensionUnmatched(doc));
        hasDocNeedingDimension = true;
    }
    // An unresolved dimension only blocks completion while a requirement is
    // still uncovered: once everything is covered, resolving it cannot change
    // the outcome, so the user shouldn't be forced to pick one.
    const isDimensionSelectionRequired =
        hasDocNeedingDimension && uncoveredKeys.length > 0;

    return {
        rows,
        unmatched,
        uncoveredKeys,
        isDimensionSelectionRequired,
        canComplete: uncoveredKeys.length === 0,
    };
}
